import { useState, useEffect } from 'react'
import { getLocalApiUrl, authFetch } from '../../../config/api'

/**
 * Reference UI with mixed mock + optional live runners — gated by
 * `VITE_ENABLE_PATRON_SCREENING_PROTOTYPE` (see `config/featureFlags.js`).
 *
 * Each row that maps to a backend runner has a `liveSourceId` — this is the `source` value
 * POST /api/patron-screening/query accepts. Rows without `liveSourceId` are still mocked and
 * render with an "In progress" badge.
 */
const SOURCES = [
  { id: "interpol", liveSourceId: "interpol", cat: "Sanctions & watchlists", name: "INTERPOL Red/Orange Notices", api: "REST API", free: true,
    // Disabled by default: the public INTERPOL endpoint is fronted by Akamai's WAF and
    // frequently returns HTTP 403 to cloud/corporate egresses. OpenSanctions already
    // ingests INTERPOL Red + Yellow notices via its `default` collection, so those hits
    // flow through the OpenSanctions row instead. Users can re-enable from the Configure
    // sources panel if their egress is allowlisted.
    disabledByDefault: true,
    replacedBy: "opensanctions",
    purposes: ["criminal_history", "identity", "sanctions", "full"],
    detail: "Partial name match on Orange Notice #2023-AO-4471 — financial fraud investigation, referred by Australian Federal Police. Subject: John Andrew Smith, DOB 1978-03-15, Australian national.",
    matchFields: [
      { field: "Name", status: "exact", note: "First + last exact match" },
      { field: "DOB", status: "partial", note: "Year matches, day differs (15 vs 18)" },
      { field: "Nationality", status: "exact", note: "Australian" },
      { field: "Photo", status: "pending", note: "Awaiting reverse image check" }
    ],
    confidence: 72,
    actions: ["Fetch full notice detail", "Cross-reference DOB via ID document", "Run reverse image verification", "Request AFP case reference", "Escalate to MLRO"] },
  { id: "dfat", cat: "Sanctions & watchlists", name: "DFAT Consolidated Sanctions", api: "XML Feed", free: true, purposes: ["sanctions", "full"] },
  { id: "ofac", cat: "Sanctions & watchlists", name: "OFAC SDN List", api: "REST API", free: true, purposes: ["sanctions", "full"] },
  { id: "un_sanctions", cat: "Sanctions & watchlists", name: "UN Security Council Sanctions", api: "XML Feed", free: true, purposes: ["sanctions", "full"] },
  { id: "eu_sanctions", cat: "Sanctions & watchlists", name: "EU Consolidated Sanctions", api: "XML Feed", free: true, purposes: ["sanctions", "full"] },
  { id: "opensanctions", liveSourceId: "opensanctions", cat: "Sanctions & watchlists", name: "OpenSanctions (PEP + Sanctions)", api: "REST API", free: true, purposes: ["sanctions", "pep", "criminal_history", "full"] },

  { id: "google_news", liveSourceId: "google_news", cat: "Adverse media", name: "Google News Search", api: "SerpAPI", free: false,
    purposes: ["criminal_history", "source_of_funds", "adverse_media", "full"],
    detail: "3 articles found. (1) Herald Sun 2024-03-12: Director of Acme Holdings faces fraud charges. (2) AFR 2023-11-05: ASIC investigates Acme Holdings directors. (3) ABC News 2024-01-20: Casino patron linked to AML ring.",
    matchFields: [
      { field: "Name in headline", status: "exact", note: "2 of 3 articles" },
      { field: "Company match", status: "exact", note: "Acme Holdings Pty Ltd" },
      { field: "Jurisdiction", status: "exact", note: "Australia" }
    ],
    confidence: 88,
    actions: ["Fetch full article text", "Archive articles with timestamp", "Extract all named entities", "Cross-reference company names with ASIC", "Flag for enhanced due diligence"] },
  { id: "austrac_enforce", cat: "Adverse media", name: "AUSTRAC Enforcement Actions", api: "Web Scraper", free: true, purposes: ["criminal_history", "source_of_funds", "adverse_media", "full"] },
  { id: "court_au", liveSourceId: "nsw_caselaw", cat: "Adverse media", name: "Australian Court Judgments (NSW)", api: "REST API", free: true,
    purposes: ["criminal_history", "source_of_funds", "adverse_media", "full"],
    detail: "2 matching records. (1) Federal Court FCA [2024] 312 — ASIC v Smith & Ors, director disqualification proceedings. (2) NSW Supreme Court — ATO v Smith, tax fraud, judgment pending.",
    matchFields: [
      { field: "Name", status: "exact", note: "Full name match in both" },
      { field: "Related entity", status: "exact", note: "Acme Holdings referenced" }
    ],
    confidence: 91,
    actions: ["Download judgment PDFs", "Extract all related party names", "Check for appeals or ongoing matters", "Cross-reference with ASIC director bans", "Timeline the legal proceedings"] },

  { id: "asic", cat: "Corporate registries", name: "ASIC Company & Director Search", api: "REST API", free: false,
    purposes: ["source_of_funds", "business_associations", "address", "full"],
    detail: "Subject listed as director of 4 companies: Acme Holdings Pty Ltd (deregistered 2023), Apex Trading Co (under external administration), Zenith Ventures Pty Ltd (current, active), Blue Sky Capital (strike-off action pending ASIC). Two companies flagged with adverse notices.",
    matchFields: [
      { field: "Director name", status: "exact", note: "Current + historical" },
      { field: "DOB on record", status: "exact", note: "Matches intake DOB" },
      { field: "Address", status: "partial", note: "Same suburb, different street" }
    ],
    confidence: 94,
    actions: ["Pull full company extracts for all 4", "Map beneficial ownership chain", "Check related party cross-holdings", "Verify current status on Zenith Ventures", "Request ASIC adverse notice detail"] },
  { id: "opencorp", cat: "Corporate registries", name: "OpenCorporates", api: "REST API", free: true, purposes: ["source_of_funds", "business_associations", "full"] },
  { id: "abn", cat: "Corporate registries", name: "ABN Lookup", api: "REST API", free: true, purposes: ["business_associations", "address", "full"] },
  { id: "acra", cat: "Corporate registries", name: "ACRA (Singapore)", api: "REST API", free: false, purposes: ["source_of_funds", "business_associations", "full"] },
  { id: "mca", cat: "Corporate registries", name: "MCA (India)", api: "REST API", free: false, purposes: ["source_of_funds", "business_associations", "full"] },

  { id: "sherlock", cat: "Social & digital footprint", name: "Sherlock Username Search", api: "CLI / Python", free: true,
    purposes: ["identity", "source_of_funds", "criminal_history", "full"],
    detail: "Username 'jsmith_au' found across 14 platforms: X/Twitter, Instagram, Reddit, GitHub, Telegram, Discord, Medium, LinkedIn, Facebook, TikTok, Quora, Pinterest, Keybase, BitcoinTalk. Secondary username 'johnsmith_trades' found on 3 crypto forums: BitcoinTalk, Dread, LocalBitcoins.",
    matchFields: [
      { field: "Username pattern", status: "partial", note: "Consistent with name + country" },
      { field: "Profile photo", status: "pending", note: "Needs reverse image check" },
      { field: "Crypto forum activity", status: "flagged", note: "Active trading posts" }
    ],
    confidence: 58,
    actions: ["Screenshot all active profiles", "Analyze Telegram/Discord group memberships", "Review crypto forum posts for SOF clues", "Check for wallet addresses in posts", "Archive all profile snapshots with timestamps"] },
  { id: "reverse_img", cat: "Social & digital footprint", name: "Reverse Image Search", api: "SerpAPI", free: false, purposes: ["identity", "full"] },
  { id: "whats_my_name", cat: "Social & digital footprint", name: "WhatsMyName", api: "CLI / Python", free: true, purposes: ["identity", "full"] },
  { id: "wayback", cat: "Social & digital footprint", name: "Wayback Machine", api: "REST API", free: true, purposes: ["business_associations", "source_of_funds", "full"] },

  { id: "afsa", cat: "Financial & insolvency", name: "AFSA Insolvency Register", api: "Web Scraper", free: true,
    purposes: ["source_of_funds", "address", "criminal_history", "full"],
    detail: "Bankruptcy match confirmed. Filed 2019-08-14, discharged 2022-08-14 (standard 3-year period). Original debt: $2.3M comprising ATO debt ($890K), NAB commercial loan ($620K), trade creditors ($790K). Trustee: Hall Chadwick Sydney. Post-discharge income not assessed.",
    matchFields: [
      { field: "Full name", status: "exact", note: "John Andrew Smith" },
      { field: "DOB", status: "exact", note: "1978-03-15" },
      { field: "State", status: "exact", note: "NSW" }
    ],
    confidence: 97,
    actions: ["Download full insolvency record", "Verify discharge conditions", "Cross-reference debt with company failures", "Check post-bankruptcy asset accumulation", "Flag source-of-funds inconsistency"] },
  { id: "land_reg", cat: "Financial & insolvency", name: "Land Title Registry (State)", api: "Varies", free: false, purposes: ["source_of_funds", "address", "full"] },
  { id: "ppsr", cat: "Financial & insolvency", name: "PPSR (Personal Property)", api: "REST API", free: false, purposes: ["source_of_funds", "full"] },

  { id: "wikidata_pep", liveSourceId: "wikidata", cat: "PEP screening", name: "EveryPolitician (Wikidata)", api: "REST API", free: true, purposes: ["pep", "full"] },
  { id: "opensanc_pep", liveSourceId: "opensanctions", cat: "PEP screening", name: "OpenSanctions PEP Dataset", api: "REST API", free: true, purposes: ["pep", "full"] },

  { id: "gnews", liveSourceId: "gnews", cat: "Background search", name: "GNews API", api: "REST API", free: true, tier: "100/day free",
    purposes: ["adverse_media", "criminal_history", "source_of_funds", "full"],
    detail: "2 relevant articles found via GNews. (1) Sydney Morning Herald 2024-02-18: Former Acme Holdings director under investigation for undisclosed offshore accounts. (2) The Australian 2023-09-30: NSW gaming patron flagged in cross-border money transfer probe.",
    matchFields: [
      { field: "Name in article", status: "exact", note: "Full name match in both articles" },
      { field: "Company reference", status: "exact", note: "Acme Holdings Pty Ltd" },
      { field: "Jurisdiction", status: "exact", note: "NSW, Australia" },
      { field: "Recency", status: "flagged", note: "Most recent article < 6 months old" }
    ],
    confidence: 85,
    actions: ["Fetch full article via GNews URL", "Archive articles with timestamps", "Extract all named entities and co-accused", "Cross-reference with court judgment hits", "Add to adverse media dossier"] },
  { id: "brave_search", cat: "Background search", name: "Brave Search API", api: "REST API", free: true, tier: "2,000/mo free",
    purposes: ["adverse_media", "criminal_history", "source_of_funds", "address", "business_associations", "full"],
    detail: "5 web results found. Mix of news articles, court listings, and a LinkedIn profile. Key result: NSW Local Court listing for Smith, J.A. — mention fraud, hearing date 2024-06-15. Also found a cached corporate profile page for Zenith Ventures listing subject as director.",
    matchFields: [
      { field: "Name", status: "exact", note: "Multiple result types" },
      { field: "Court listing", status: "flagged", note: "Active hearing found" },
      { field: "Corporate profile", status: "exact", note: "Zenith Ventures confirmed" }
    ],
    confidence: 78,
    actions: ["Fetch full page content for each result", "Archive court listing detail", "Screenshot corporate profile", "Cross-reference Zenith Ventures with ASIC", "Flag active court hearing date"] },
  { id: "mediastack", cat: "Background search", name: "MediaStack", api: "REST API", free: true, tier: "500/mo free",
    purposes: ["adverse_media", "criminal_history", "full"],
    detail: "1 result from Australian media. ABC News online 2024-01-20: Report on casino patron network linked to structured cash deposits across multiple venues in regional NSW.",
    matchFields: [
      { field: "Name reference", status: "partial", note: "Surname match, no first name in headline" },
      { field: "Location", status: "exact", note: "Regional NSW gaming venues" }
    ],
    confidence: 52,
    actions: ["Fetch full article text", "Verify name match in article body", "Cross-reference venue names with WEG locations", "Check for AUSTRAC media releases on same topic"] },
  { id: "duckduckgo", cat: "Background search", name: "DuckDuckGo Instant API", api: "REST API", free: true, tier: "Unlimited",
    purposes: ["identity", "adverse_media", "full"],
    detail: "Abstract and related topics returned. Wikipedia disambiguation page for 'John Smith' — no unique match. Related topics include several Australian business figures but none confirmed as subject.",
    matchFields: [
      { field: "Name", status: "partial", note: "Common name, disambiguation needed" }
    ],
    confidence: 15,
    actions: ["Dismiss — insufficient match confidence", "Retry with full name + company", "Use as supplementary context only"] },
  { id: "google_alerts", cat: "Background search", name: "Google Alerts (Ongoing)", api: "Email/RSS", free: true, tier: "Unlimited",
    // No public Google Alerts API — catalog/mock only (manual RSS/email monitoring).
    purposes: ["adverse_media", "criminal_history", "ongoing_monitoring", "full"],
    detail: "Monitoring alert configured. Will trigger email notification when new web content matching subject name + key terms (fraud, ASIC, court, bankrupt, laundering) is published.",
    matchFields: [],
    confidence: 0,
    actions: ["Configure alert for patron name", "Add company name as separate alert", "Set up email parser for automated ingestion", "Route alerts to compliance dashboard"] },
  { id: "newsapi", cat: "Background search", name: "NewsAPI.org", api: "REST API", free: true, tier: "100/day free (dev)", purposes: ["adverse_media", "criminal_history", "full"] },
  { id: "google_cse", liveSourceId: "google_cse", cat: "Background search", name: "Google Search (SerpAPI)", api: "SerpAPI", free: false, tier: "Plan-based (serpapi.com)", purposes: ["adverse_media", "criminal_history", "source_of_funds", "address", "business_associations", "full"] },
];

const CAT_STYLES = {
  "Sanctions & watchlists": { bg: "#FAECE7", text: "#712B13", border: "#F0997B" },
  "Adverse media": { bg: "#FBEAF0", text: "#72243E", border: "#ED93B1" },
  "Corporate registries": { bg: "#E6F1FB", text: "#0C447C", border: "#85B7EB" },
  "Social & digital footprint": { bg: "#EEEDFE", text: "#3C3489", border: "#AFA9EC" },
  "Financial & insolvency": { bg: "#FAEEDA", text: "#633806", border: "#EF9F27" },
  "PEP screening": { bg: "#E1F5EE", text: "#085041", border: "#5DCAA5" },
  "Background search": { bg: "#E6F1FB", text: "#042C53", border: "#378ADD" },
};

const INVESTIGATION_PURPOSES = [
  { id: "full", label: "Full screening", desc: "Run all sources", icon: "///", color: "var(--psg-teal)" },
  { id: "source_of_funds", label: "Source of funds", desc: "Trace wealth origin, assets, bankruptcy, company ownership", icon: "$", color: "#633806" },
  { id: "address", label: "Address verification", desc: "Confirm residential or business address", icon: "@", color: "#0C447C" },
  { id: "identity", label: "Identity verification", desc: "Confirm person is who they claim to be", icon: "ID", color: "#3C3489" },
  { id: "criminal_history", label: "Criminal & legal history", desc: "Court records, warrants, enforcement actions", icon: "!", color: "#791F1F" },
  { id: "sanctions", label: "Sanctions check", desc: "DFAT, OFAC, UN, EU, INTERPOL", icon: "X", color: "#A32D2D" },
  { id: "pep", label: "PEP screening", desc: "Politically exposed persons", icon: "P", color: "#085041" },
  { id: "adverse_media", label: "Adverse media", desc: "News, media mentions, public records", icon: "N", color: "#72243E" },
  { id: "business_associations", label: "Business associations", desc: "Company directorships, beneficial ownership", icon: "B", color: "#0C447C" },
  { id: "ongoing_monitoring", label: "Ongoing monitoring", desc: "Set up alerts for future activity", icon: "~", color: "#854F0B" },
];

const STATUS_MAP = {
  exact: { label: "Exact", bg: "#EAF3DE", color: "#27500A" },
  partial: { label: "Partial", bg: "#FAEEDA", color: "#633806" },
  pending: { label: "Pending", bg: "#F1EFE8", color: "#5F5E5A" },
  flagged: { label: "Flagged", bg: "#FCEBEB", color: "#791F1F" },
};

function ConfidenceBar({ value }) {
  const color = value >= 85 ? "#A32D2D" : value >= 60 ? "#854F0B" : "#5F5E5A";
  const label = value >= 85 ? "High confidence" : value >= 60 ? "Medium confidence" : "Low confidence";
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: "#5F5E5A" }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color }}>{value}%</span>
      </div>
      <div style={{ height: 6, background: "#F1EFE8", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function MatchFieldsTable({ fields }) {
  return (
    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginBottom: 16 }}>
      <tbody>
        {fields.map((f, i) => {
          const st = STATUS_MAP[f.status];
          return (
            <tr key={i} style={{ borderBottom: "1px solid #F1EFE8" }}>
              <td style={{ padding: "6px 0", color: "#5F5E5A", width: 100 }}>{f.field}</td>
              <td style={{ padding: "6px 8px" }}>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: st.bg, color: st.color, fontWeight: 500 }}>{st.label}</span>
              </td>
              <td style={{ padding: "6px 0", color: "#888780", fontSize: 12 }}>{f.note}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DrillDownPanel({ source, onAction, liveStatus }) {
  const isDemo = liveStatus === 'in_progress' || (!source._live && !source.liveSourceId);
  return (
    <div style={{ margin: "0 0 8px", padding: 20, background: "#FAFAF8", borderRadius: 10, borderLeft: "3px solid var(--psg-teal)" }}>
      {isDemo && (
        <p style={{
          fontSize: 12, color: "#633806", background: "#FAEEDA", borderRadius: 6,
          padding: "8px 10px", margin: "0 0 12px", lineHeight: 1.5,
        }}>
          <strong>Demo sample only.</strong> This source is not integrated — no API call was made.
          Disable it under Patron Screening Settings if you do not need it in the catalog.
        </p>
      )}
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 6px", color: "var(--psg-teal)" }}>Finding summary</p>
        <p style={{ fontSize: 13, color: "#5F5E5A", margin: 0, lineHeight: 1.7 }}>{source.detail}</p>
      </div>

      <ConfidenceBar value={source.confidence} />
      
      <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 8px", color: "var(--psg-teal)" }}>Field-level match</p>
      <MatchFieldsTable fields={source.matchFields} />
      
      <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 8px", color: "var(--psg-teal)" }}>Investigation actions</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {source.actions.map((a, i) => (
          <button key={i} onClick={() => onAction(a)} style={{
            fontSize: 12, padding: "6px 12px", borderRadius: 6,
            border: "1px solid #D3D1C7", background: "#fff", color: "#1f2937",
            cursor: "pointer", transition: "all 0.15s"
          }}
          onMouseEnter={e => { e.target.style.background = "#F1EFE8"; }}
          onMouseLeave={e => { e.target.style.background = "#fff"; }}
          >{a}</button>
        ))}
      </div>
    </div>
  );
}

/** Render Live / Needs key / In progress next to each row. */
function LiveStatusBadge({ status }) {
  if (status === 'live') {
    return (
      <span title="Calls a real API" style={{
        fontSize: 10, padding: "2px 8px", borderRadius: 6,
        background: "#E1F5EE", color: "#085041", fontWeight: 600, letterSpacing: 0.2,
      }}>Live</span>
    );
  }
  if (status === 'needs_key') {
    return (
      <span title="Live, but this tenant has no API key configured" style={{
        fontSize: 10, padding: "2px 8px", borderRadius: 6,
        background: "#FAEEDA", color: "#633806", fontWeight: 600, letterSpacing: 0.2,
      }}>Needs key</span>
    );
  }
  return (
    <span title="Mocked — real integration pending" style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 6,
      background: "#F1EFE8", color: "#5F5E5A", fontWeight: 600, letterSpacing: 0.2,
    }}>In progress</span>
  );
}

/**
 * Modal that shows the raw records returned by a live OSINT source,
 * plus the HTTP metadata envelope from the backend.
 */
function ReturnedRecordsModal({ source, onClose }) {
  const envelope = source._liveEnvelope || {};
  const data = envelope.data || {};

  // OpenSanctions batch-match: data.responses.<q>.results
  // GNews: data.articles
  // SerpAPI Google: data.relevant (name-matched) then data.items (organic/news)
  // Generic: data.results
  const results = Array.isArray(data?.responses?.subject?.results)
    ? data.responses.subject.results
    : Array.isArray(data?.responses?.q1?.results)
      ? data.responses.q1.results
      : Array.isArray(data?.articles)
        ? data.articles
        : Array.isArray(data?.relevant) && data.relevant.length > 0
          ? data.relevant
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: "100%", maxWidth: 900,
          maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #E8E7E2",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: "var(--psg-teal)", fontWeight: 600 }}>
              {source.name} — Returned Records
            </h3>
            <div style={{ fontSize: 11, color: "#888780", marginTop: 4 }}>
              {envelope.summary} {envelope.httpStatus && `· HTTP ${envelope.httpStatus}`}
              {envelope.responseTimeMs != null && ` · ${envelope.responseTimeMs}ms`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "none", fontSize: 24,
              color: "#888780", cursor: "pointer", padding: 0, lineHeight: 1,
            }}
          >×</button>
        </div>

        <div style={{ padding: 20 }}>
          {results.length === 0 ? (
            <div style={{
              padding: 16, background: "#FAFAF8", borderRadius: 8, fontSize: 13,
              color: "#5F5E5A", textAlign: "center",
            }}>
              No structured records in response. See raw payload below.
            </div>
          ) : (
            <>
              <div style={{
                fontSize: 12, color: "#5F5E5A", marginBottom: 12, fontWeight: 500,
              }}>
                {results.length} record{results.length === 1 ? "" : "s"} returned
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {results.map((r, i) => (
                  <RecordCard key={i} record={r} index={i} />
                ))}
              </div>
            </>
          )}

          <details style={{ marginTop: 20 }}>
            <summary style={{
              cursor: "pointer", fontSize: 12, fontWeight: 500, color: "var(--psg-teal)",
              padding: "8px 0",
            }}>
              Raw JSON response
            </summary>
            <pre style={{
              marginTop: 8, padding: 12, background: "#1a1a1a", color: "#e0e0e0",
              borderRadius: 6, fontSize: 11, lineHeight: 1.5, overflow: "auto",
              maxHeight: 400,
            }}>{JSON.stringify(data, null, 2)}</pre>
          </details>
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "1px solid #E8E7E2",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px", borderRadius: 6, border: "1px solid #D3D1C7",
              background: "#fff", color: "#1f2937", fontSize: 13, cursor: "pointer",
            }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

/** Display a single result record — tries OpenSanctions shape first, then falls back to key/value. */
function RecordCard({ record, index }) {
  // OpenSanctions shape: { id, caption, schema, score, match, properties, datasets, ... }
  const isOpenSanctions = record && (record.caption || record.schema) && record.properties;
  // GNews shape: { title, description, url, image, publishedAt, source: { name, url } }
  const isGNews = record && record.title && record.url && (record.publishedAt || record.source);
  // SerpAPI / mapped Google: { title, link|url, snippet, source, date }
  const serpLink = record?.link || record?.url || null;
  const isSerpApi = record && record.title && serpLink && (record.snippet != null || record.source != null || record.date != null);

  if (isOpenSanctions) {
    const props = record.properties || {};
    const scorePct = typeof record.score === "number" ? Math.round(record.score * 100) : null;
    return (
      <div style={{
        border: "1px solid #E8E7E2", borderRadius: 8, padding: 12, background: "#FAFAF8",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937", marginBottom: 4 }}>
              {record.caption || "(unnamed)"}
            </div>
            <div style={{ fontSize: 11, color: "#888780", marginBottom: 8 }}>
              {record.schema} {record.id && `· ${record.id}`}
            </div>
          </div>
          {scorePct != null && (
            <div style={{
              fontSize: 11, fontWeight: 600, padding: "4px 8px", borderRadius: 6,
              background: scorePct >= 85 ? "#FCEBEB" : scorePct >= 60 ? "#FDF3E4" : "#EAF3DE",
              color: scorePct >= 85 ? "#791F1F" : scorePct >= 60 ? "#633806" : "#27500A",
              whiteSpace: "nowrap",
            }}>
              {scorePct}% {record.match ? " · MATCH" : ""}
            </div>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, fontSize: 12 }}>
          {Object.entries(props).slice(0, 10).map(([k, v]) => (
            <div key={k} style={{ display: "contents" }}>
              <div style={{ color: "#888780", fontWeight: 500 }}>{k}</div>
              <div style={{ color: "#1f2937", wordBreak: "break-word" }}>
                {Array.isArray(v) ? v.join(", ") : String(v)}
              </div>
            </div>
          ))}
        </div>
        {Array.isArray(record.datasets) && record.datasets.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
            {record.datasets.map((d) => (
              <span key={d} style={{
                fontSize: 10, padding: "2px 6px", borderRadius: 4,
                background: "#E6F1FB", color: "#0C447C",
              }}>{d}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isGNews) {
    return (
      <div style={{
        border: "1px solid #E8E7E2", borderRadius: 8, padding: 12, background: "#FAFAF8",
      }}>
        <a href={record.url} target="_blank" rel="noopener noreferrer" style={{
          fontWeight: 600, fontSize: 14, color: "var(--psg-teal)", textDecoration: "none",
        }}>
          {record.title}
        </a>
        <div style={{ fontSize: 11, color: "#888780", marginTop: 4, marginBottom: 6 }}>
          {record.source?.name || record.source} · {record.publishedAt}
        </div>
        {record.description && (
          <div style={{ fontSize: 12, color: "#5F5E5A", lineHeight: 1.6 }}>
            {record.description}
          </div>
        )}
      </div>
    );
  }

  if (isSerpApi) {
    return (
      <div style={{
        border: "1px solid #E8E7E2", borderRadius: 8, padding: 12, background: "#FAFAF8",
      }}>
        <a href={serpLink} target="_blank" rel="noopener noreferrer" style={{
          fontWeight: 600, fontSize: 14, color: "var(--psg-teal)", textDecoration: "none",
        }}>
          {record.title}
        </a>
        <div style={{ fontSize: 11, color: "#888780", marginTop: 4, marginBottom: 6 }}>
          {[record.source, record.date].filter(Boolean).join(" · ") || serpLink}
        </div>
        {(record.snippet || record.description) && (
          <div style={{ fontSize: 12, color: "#5F5E5A", lineHeight: 1.6 }}>
            {record.snippet || record.description}
          </div>
        )}
      </div>
    );
  }

  // Generic fallback
  return (
    <div style={{
      border: "1px solid #E8E7E2", borderRadius: 8, padding: 12, background: "#FAFAF8",
    }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#888780", marginBottom: 6 }}>
        Record #{index + 1}
      </div>
      <pre style={{
        margin: 0, fontSize: 11, color: "#1f2937", whiteSpace: "pre-wrap",
        wordBreak: "break-word", lineHeight: 1.5,
      }}>{JSON.stringify(record, null, 2)}</pre>
    </div>
  );
}

/** Overall outcome status for a source row after scan. */
function outcomeStatus(source, { scanned = false } = {}) {
  if (source._live && source._liveStatus) return source._liveStatus;
  if (scanned) {
    if (!source.liveSourceId) return 'not_integrated';
    if (source.liveSourceId && !source._live) return 'not_run';
  }
  return source.detail ? 'hit' : 'clear';
}

const OUTCOME_STYLES = {
  hit:   { label: 'Hit',   bg: '#FCEBEB', color: '#791F1F' },
  clear: { label: 'Clear', bg: '#EAF3DE', color: '#27500A' },
  error: { label: 'Error', bg: '#FAEEDA', color: '#633806' },
  not_integrated: { label: 'Not integrated', bg: '#F1EFE8', color: '#5F5E5A' },
  not_run: { label: 'Not run', bg: '#FAEEDA', color: '#633806' },
};

function SourceRow({ source, expanded, onToggle, liveStatus, scanned = false }) {
  const cat = CAT_STYLES[source.cat];
  const [showRecords, setShowRecords] = useState(false);

  const status = outcomeStatus(source, { scanned });
  const isHit = status === 'hit';
  const isError = status === 'error';
  const isExpandable = (isHit || isError) && !!source.detail;
  const badge = OUTCOME_STYLES[status] || OUTCOME_STYLES.clear;

  const handleAction = (action) => {
    if (action === "Review returned records") {
      setShowRecords(true);
      return;
    }
    if (action === "Check API key & retry") {
      window.open("/compliance-safe/settings/patron-screening?tab=keys", "_blank");
      return;
    }
    if (action === "Review backend logs") {
      console.log("[PatronScreening] envelope for", source.name, source._liveEnvelope);
      alert("Envelope logged to browser console. Check DevTools → Console.");
      return;
    }
    if (source._live) {
      alert(`"${action}" is not wired yet. The live API result is already shown above — use Review returned records for raw data.`);
      return;
    }
    alert(`"${action}" is demo-only. This source is not integrated yet — no API call was made.`);
  };

  return (
    <div>
      <div
        onClick={isExpandable ? onToggle : undefined}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", borderRadius: 8, marginBottom: 2,
          border: expanded ? "1px solid rgba(10, 81, 105, 0.4)" : "1px solid #E8E7E2",
          background: expanded ? "#FAFAF8" : "transparent",
          cursor: isExpandable ? "pointer" : "default",
          transition: "all 0.15s"
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#1f2937" }}>{source.name}</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: cat.bg, color: cat.text, fontWeight: 500 }}>{source.cat}</span>
            <LiveStatusBadge status={liveStatus} />
            {source.free && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "#EAF3DE", color: "#27500A" }}>Free</span>}
            {source.tier && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "#E6F1FB", color: "#0C447C" }}>{source.tier}</span>}
          </div>
          {isHit && source.confidence > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <div style={{ width: 60, height: 4, background: "#E8E7E2", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${source.confidence}%`, height: "100%", background: source.confidence >= 85 ? "#A32D2D" : "#854F0B", borderRadius: 2 }} />
              </div>
              <span style={{ fontSize: 11, color: "#888780" }}>{source.confidence}% match</span>
            </div>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: "#888780", background: "#F1EFE8", padding: "2px 8px", borderRadius: 6 }}>{source.api}</span>
          <span style={{
            fontSize: 11, padding: "3px 10px", borderRadius: 6, fontWeight: 500,
            background: badge.bg, color: badge.color,
          }}>{badge.label}</span>
          {isExpandable && (
            <span style={{ fontSize: 14, color: "#888780", transform: expanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", display: "inline-block" }}>&#9662;</span>
          )}
        </div>
      </div>
      {expanded && isExpandable && (
        <DrillDownPanel source={source} onAction={handleAction} liveStatus={liveStatus} />
      )}
      {showRecords && (
        <ReturnedRecordsModal source={source} onClose={() => setShowRecords(false)} />
      )}
    </div>
  );
}

function AuditTrail({ entries }) {
  return (
    <div style={{ marginTop: 20, padding: 16, background: "#FAFAF8", borderRadius: 10, border: "1px solid #E8E7E2" }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 10px", color: "#5F5E5A" }}>Audit trail</p>
      {entries.map((e, i) => (
        <div key={i} style={{ display: "flex", gap: 12, fontSize: 12, lineHeight: 1.8, color: "#5F5E5A" }}>
          <span style={{ fontFamily: "monospace", color: "#B4B2A9", whiteSpace: "nowrap" }}>{e.time}</span>
          <span>{e.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Derive per-row status from backend readiness map. */
function deriveLiveStatus(source, readyMap) {
  if (!source.liveSourceId) return 'in_progress';
  const info = readyMap.get(source.liveSourceId);
  if (!info) return 'in_progress';
  if (info.ready) return 'live';
  return info.requiresApiKey ? 'needs_key' : 'in_progress';
}

/** Strip HTML tags and decode common entities so raw WAF error pages don't leak into the UI. */
function sanitizeForDisplay(str) {
  if (!str) return '';
  const noTags = String(str).replace(/<[^>]*>/g, ' ');
  return noTags
    .replace(/&#\d+;/g, (m) => {
      const code = parseInt(m.slice(2, -1), 10);
      return Number.isNaN(code) ? m : String.fromCharCode(code);
    })
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Turn a backend envelope into the shape SourceRow / DrillDownPanel expect. */
function overlayFromEnvelope(source, envelope) {
  if (!envelope) return source;
  const { status, summary, httpStatus, data } = envelope;
  const isHit = status === 'hit';
  const isError = status === 'error';
  const cleanSummary = sanitizeForDisplay(summary);
  const detailPieces = [cleanSummary || ''];
  if (httpStatus) detailPieces.push(`HTTP ${httpStatus}`);
  const detail = isHit || isError ? detailPieces.filter(Boolean).join(' — ') : null;

  const matchFields = [];
  if (isError) {
    matchFields.push({ field: 'Status', status: 'flagged', note: cleanSummary || 'Error' });
  }

  const actions = [];
  if (isHit) actions.push('Review returned records', 'Archive finding with timestamp');
  if (isError) actions.push('Check API key & retry', 'Review backend logs');

  return {
    ...source,
    detail,
    matchFields,
    confidence: isHit ? 60 : 0,
    actions,
    _live: true,
    _liveStatus: status, // 'hit' | 'clear' | 'error' — from backend envelope
    _liveEnvelope: envelope,
  };
}

export default function PatronScreeningPrototype({ initialFirstName = '', initialLastName = '', patronId = null }) {
  const [tab, setTab] = useState("hits");
  const [expanded, setExpanded] = useState(null);
  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [form, setForm] = useState({
    firstName: "", lastName: "", dob: "", country: "AU",
    idNumber: "", aliases: "", company: ""
  });
  const [purpose, setPurpose] = useState("full");
  const [enabledSourceIds, setEnabledSourceIds] = useState(() => new Set());
  const [enabledPurposeIds, setEnabledPurposeIds] = useState(() => new Set());
  const [catalogSettingsLoaded, setCatalogSettingsLoaded] = useState(false);
  // liveReadyMap: liveSourceId -> { ready, requiresApiKey }
  const [liveReadyMap, setLiveReadyMap] = useState(() => new Map());
  // liveResults: frontend SOURCES[].id -> envelope from /api/patron-screening/query
  const [liveResults, setLiveResults] = useState(() => new Map());
  // Persistence state: 'idle' | 'saving' | 'saved' | 'error'
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedScanUuid, setSavedScanUuid] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // History panel
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(null);

  // Load tenant catalog (sources + purposes enabled in settings)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/patron-screening/settings'));
        const json = await res.json().catch(() => ({}));
        if (cancelled || !json?.success) {
          if (!cancelled) {
            setEnabledSourceIds(new Set(SOURCES.filter((s) => !s.disabledByDefault).map((s) => s.id)));
            setEnabledPurposeIds(new Set(INVESTIGATION_PURPOSES.map((p) => p.id)));
            setCatalogSettingsLoaded(true);
          }
          return;
        }
        const srcIds = new Set((json.sources || []).filter((s) => s.isEnabled).map((s) => s.id));
        const purIds = new Set((json.purposes || []).filter((p) => p.isEnabled).map((p) => p.id));
        setEnabledSourceIds(srcIds);
        setEnabledPurposeIds(purIds);
        setCatalogSettingsLoaded(true);
      } catch (_) {
        if (!cancelled) {
          setEnabledSourceIds(new Set(SOURCES.filter((s) => !s.disabledByDefault).map((s) => s.id)));
          setEnabledPurposeIds(new Set(INVESTIGATION_PURPOSES.map((p) => p.id)));
          setCatalogSettingsLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const availablePurposes = INVESTIGATION_PURPOSES.filter((p) => enabledPurposeIds.has(p.id));

  useEffect(() => {
    if (availablePurposes.length === 0) return;
    if (!enabledPurposeIds.has(purpose)) {
      setPurpose(availablePurposes[0].id);
    }
  }, [availablePurposes, enabledPurposeIds, purpose]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      firstName: initialFirstName,
      lastName: initialLastName,
    }))
  }, [initialFirstName, initialLastName])

  // Load backend source readiness once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/patron-screening/sources'));
        const json = await res.json().catch(() => ({}));
        if (cancelled || !json?.success || !Array.isArray(json.sources)) return;
        const m = new Map();
        for (const s of json.sources) {
          m.set(s.id, { ready: !!s.ready, requiresApiKey: !!s.requiresApiKey });
        }
        setLiveReadyMap(m);
      } catch (_) {
        // Keep empty map; everything will render as "In progress"
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Merge the static SOURCES row with any live envelope we received for it. */
  const effectiveSources = SOURCES.map((s) => {
    const env = liveResults.get(s.id);
    if (env) return overlayFromEnvelope(s, env);
    // If the row was live but got no envelope (scan didn't run), keep the mock detail
    return s;
  });

  const isEligibleForPurpose = (s) => s.purposes && s.purposes.includes(purpose);
  const catalogSources = effectiveSources.filter((s) => enabledSourceIds.has(s.id));
  const filteredSources = catalogSources.filter((s) => isEligibleForPurpose(s));
  const skippedSources = catalogSources.filter((s) => !isEligibleForPurpose(s));
  const statusOpts = { scanned };
  const bucketedByStatus = {
    hit: filteredSources.filter((s) => outcomeStatus(s, statusOpts) === 'hit'),
    clear: filteredSources.filter((s) => outcomeStatus(s, statusOpts) === 'clear'),
    error: filteredSources.filter((s) => outcomeStatus(s, statusOpts) === 'error'),
    not_integrated: filteredSources.filter((s) => outcomeStatus(s, statusOpts) === 'not_integrated'),
    not_run: filteredSources.filter((s) => outcomeStatus(s, statusOpts) === 'not_run'),
  };
  const hits = bucketedByStatus.hit;
  const clear = bucketedByStatus.clear;
  const errorsByUpstream = [...bucketedByStatus.error, ...bucketedByStatus.not_run];
  const notIntegrated = bucketedByStatus.not_integrated;

  const selectedPurpose = availablePurposes.find(p => p.id === purpose) || availablePurposes[0];
  const liveCount = filteredSources.filter((s) => deriveLiveStatus(s, liveReadyMap) === 'live').length;
  const nowTime = () => {
    const d = new Date();
    return d.toLocaleTimeString(undefined, { hour12: false });
  };
  const auditEntries = scanned ? [
    { time: nowTime(), text: `Scan completed — subject: ${form.firstName || "—"} ${form.lastName || "—"} — purpose: ${selectedPurpose?.label || "Full screening"}` },
    { time: nowTime(), text: `${filteredSources.length} of ${catalogSources.length} tenant-enabled sources${skippedSources.length > 0 ? ` (${skippedSources.length} skipped by purpose)` : ""} · ${liveCount} live call(s), remainder mocked (in progress)` },
    { time: nowTime(), text: `${hits.length} hit(s), ${clear.length} clear${errorsByUpstream.length > 0 ? `, ${errorsByUpstream.length} error/not-run` : ""}${notIntegrated.length > 0 ? `, ${notIntegrated.length} not integrated (demo catalog only)` : ""}` },
  ] : [];

  async function runScan() {
    setScanning(true);
    setScanProgress(0);
    setLiveResults(new Map());
    setSaveStatus("idle");
    setSavedScanUuid(null);
    setSaveError(null);
    try {
      const liveRows = filteredSources.filter((s) =>
        s.liveSourceId && liveReadyMap.get(s.liveSourceId)?.ready
      );
      const totalSteps = Math.max(1, filteredSources.length);
      let done = 0;
      const tick = () => {
        done += 1;
        setScanProgress(Math.min(100, Math.round((done / totalSteps) * 100)));
      };

      // Fire live calls in parallel
      const liveResultEntries = await Promise.all(liveRows.map(async (row) => {
        try {
          const res = await authFetch(getLocalApiUrl('/api/patron-screening/query'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              source: row.liveSourceId,
              firstName: form.firstName || '',
              lastName: form.lastName || '',
              nationality: form.country || '',
              dateOfBirth: form.dob || '',
            }),
          });
          const json = await res.json().catch(() => ({}));
          tick();
          if (json?.success && json.result) return [row.id, json.result];
          return [row.id, {
            sourceId: row.liveSourceId,
            status: 'error',
            summary: json?.error || `HTTP ${res.status}`,
            error: json?.error || null,
          }];
        } catch (e) {
          tick();
          return [row.id, {
            sourceId: row.liveSourceId,
            status: 'error',
            summary: e?.message || 'Network error',
            error: e?.message || 'Network error',
          }];
        }
      }));

      // Keep the progress-bar feel consistent for non-live rows
      const mockedCount = Math.max(0, filteredSources.length - liveRows.length);
      for (let i = 0; i < mockedCount; i += 1) {
        await new Promise((r) => setTimeout(r, 40));
        tick();
      }

      setLiveResults(new Map(liveResultEntries));
      setScanned(true);
      setScanProgress(100);
    } finally {
      setScanning(false);
    }
  }

  // Build the payload that /save expects from the current scan state.
  function buildSavePayload() {
    const findings = filteredSources.map((s) => {
      const env = s._liveEnvelope || {};
      return {
        sourceId: s.liveSourceId || s.id,
        sourceName: s.name,
        category: s.cat,
        status: s._liveStatus || (s._live ? "clear" : "in_progress"),
        summary: sanitizeForDisplay(env.summary) || s.detail || null,
        httpStatus: env.httpStatus != null ? env.httpStatus : null,
        confidence: s.confidence != null ? s.confidence : null,
        recordCount: env.data && typeof env.data === 'object'
          ? (Array.isArray(env.data.results) ? env.data.results.length
            : Array.isArray(env.data.articles) ? env.data.articles.length
            : Array.isArray(env.data.bindings) ? env.data.bindings.length
            : null)
          : null,
        payload: env.data || null,
      };
    });
    const envelope = {
      scannedAt: new Date().toISOString(),
      purpose,
      form,
      liveResults: Array.from(liveResults.entries()).reduce((acc, [k, v]) => {
        acc[k] = v; return acc;
      }, {}),
      auditEntries,
    };
    return {
      patronId: patronId || null,
      purpose,
      subject: {
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        dob: form.dob || null,
        country: form.country || null,
        aliases: form.aliases || null,
        company: form.company || null,
        idNumber: form.idNumber || null,
      },
      sourcesRequested: filteredSources.map((s) => s.id),
      findings,
      riskScore,
      envelope,
    };
  }

  async function handleSaveScan() {
    setSaveStatus("saving");
    setSaveError(null);
    try {
      const res = await authFetch(getLocalApiUrl('/api/patron-screening/save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildSavePayload()),
      });
      const json = await res.json().catch(() => ({}));
      if (!json?.success) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setSaveStatus("saved");
      setSavedScanUuid(json.scanUuid || null);
      // refresh history if it's already open
      if (historyOpen) loadHistory();
    } catch (e) {
      setSaveStatus("error");
      setSaveError(e?.message || 'Save failed');
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const q = new URLSearchParams();
      if (patronId) q.set('patronId', String(patronId));
      else if (form.lastName) q.set('search', form.lastName);
      q.set('limit', '20');
      const res = await authFetch(getLocalApiUrl(`/api/patron-screening/history?${q.toString()}`));
      const json = await res.json().catch(() => ({}));
      if (!json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setHistory(Array.isArray(json.scans) ? json.scans : []);
    } catch (e) {
      setHistoryError(e?.message || 'Failed to load history');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history.length === 0 && !historyLoading) loadHistory();
  }

  const riskScore = hits.length >= 4 ? "High" : hits.length >= 2 ? "Medium" : "Low";
  const riskColor = riskScore === "High" ? "#A32D2D" : riskScore === "Medium" ? "#854F0B" : "#27500A";

  return (
    <div style={{ fontFamily: "inherit", maxWidth: 780, margin: "0 auto", color: "#1f2937" }}>
      {/* Intake form */}
      <div style={{ padding: 24, borderRadius: 12, border: "1px solid #E8E7E2", marginBottom: 24, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
          <p style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Subject intake</p>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <button type="button" onClick={toggleHistory}
              style={{ fontSize: 12, color: "var(--psg-teal)", background: "none", border: "none",
                       padding: 0, cursor: "pointer", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline" }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none" }}>
              {historyOpen ? "Hide history" : "Previous scans"} {history.length > 0 ? `(${history.length})` : ""}
            </button>
            <a href="/compliance-safe/settings/patron-screening"
              style={{ fontSize: 12, color: "var(--psg-teal)", textDecoration: "none", whiteSpace: "nowrap" }}
              onMouseEnter={(e) => { e.target.style.textDecoration = "underline" }}
              onMouseLeave={(e) => { e.target.style.textDecoration = "none" }}>
              Screening settings →
            </a>
          </div>
        </div>

        {historyOpen && (
          <div style={{
            margin: "10px 0 14px", padding: 12, borderRadius: 8,
            border: "1px solid #E8E7E2", background: "#FAFAF8"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: "#444441" }}>
                {patronId ? `Previous scans for patron ${patronId}` : "Recent scans (this tenant)"}
              </p>
              <button type="button" onClick={loadHistory} disabled={historyLoading}
                style={{ fontSize: 11, color: "var(--psg-teal)", background: "none", border: "none",
                         cursor: historyLoading ? "wait" : "pointer" }}>
                {historyLoading ? "Loading..." : "Refresh"}
              </button>
            </div>
            {historyError && (
              <p style={{ fontSize: 12, color: "#A32D2D", margin: "4px 0" }}>Error: {historyError}</p>
            )}
            {!historyLoading && history.length === 0 && !historyError && (
              <p style={{ fontSize: 12, color: "#888780", margin: "4px 0" }}>No previous scans recorded.</p>
            )}
            {history.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {history.map((h) => {
                  const when = h.createdAt ? new Date(h.createdAt).toLocaleString() : "";
                  const subj = [h.subject?.firstName, h.subject?.lastName].filter(Boolean).join(" ") || "—";
                  const riskBg = h.riskScore === "High" ? "#FCEBEB"
                    : h.riskScore === "Medium" ? "#FAEEDA" : "#EAF3DE";
                  const riskFg = h.riskScore === "High" ? "#791F1F"
                    : h.riskScore === "Medium" ? "#633806" : "#27500A";
                  return (
                    <div key={h.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 10px", background: "#fff", borderRadius: 6,
                      border: "1px solid #E8E7E2", fontSize: 12
                    }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <span style={{ fontWeight: 500, color: "#1f2937" }}>{subj}</span>
                        <span style={{ color: "#888780", fontSize: 11 }}>
                          {when} · purpose: {h.purpose || "full"}
                          {h.createdBy ? ` · by ${h.createdBy}` : ""}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                        <span title="Hits" style={{ padding: "2px 8px", borderRadius: 10, background: "#FCEBEB", color: "#791F1F", fontWeight: 500 }}>
                          {h.sourcesHit} hit
                        </span>
                        <span title="Clear" style={{ padding: "2px 8px", borderRadius: 10, background: "#EAF3DE", color: "#27500A", fontWeight: 500 }}>
                          {h.sourcesClear} clear
                        </span>
                        {h.sourcesError > 0 && (
                          <span title="Errors" style={{ padding: "2px 8px", borderRadius: 10, background: "#FAEEDA", color: "#633806", fontWeight: 500 }}>
                            {h.sourcesError} err
                          </span>
                        )}
                        {h.riskScore && (
                          <span title="Risk score" style={{ padding: "2px 8px", borderRadius: 10, background: riskBg, color: riskFg, fontWeight: 500 }}>
                            {h.riskScore}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <p style={{ fontSize: 13, color: "#888780", margin: "0 0 12px" }}>
          Enter details once — search {filteredSources.length} relevant sources{purpose !== "full" ? ` for ${selectedPurpose?.label.toLowerCase()}` : ""}.
          {' '}<span style={{ color: "#085041", fontWeight: 600 }}>Live</span> sources call real APIs;{' '}
          <span style={{ color: "#633806", fontWeight: 600 }}>Needs key</span> sources require an API key;{' '}
          <span style={{ color: "#5F5E5A", fontWeight: 600 }}>In progress</span> are still demo data.
        </p>
        
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>First name *</label>
            <input value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} placeholder="John"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>Last name *</label>
            <input value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} placeholder="Smith"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>Date of birth</label>
            <input type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})}
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>Country</label>
            <select value={form.country} onChange={e => setForm({...form, country: e.target.value})}
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none", background: "#fff" }}>
              <option value="AU">Australia</option>
              <option value="IN">India</option>
              <option value="SG">Singapore</option>
              <option value="AE">UAE</option>
              <option value="US">United States</option>
              <option value="GB">United Kingdom</option>
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>ID / passport number</label>
            <input value={form.idNumber} onChange={e => setForm({...form, idNumber: e.target.value})} placeholder="PA1234567"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>Known aliases</label>
            <input value={form.aliases} onChange={e => setForm({...form, aliases: e.target.value})} placeholder="Johnny S, J. Smith"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 4 }}>Company / entity</label>
          <input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Acme Holdings Pty Ltd"
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 6, border: "1px solid #D3D1C7", fontSize: 14, outline: "none" }} />
        </div>

        {/* Investigation purpose selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: "#888780", display: "block", marginBottom: 8 }}>What are you investigating?</label>
          {!catalogSettingsLoaded ? (
            <p style={{ fontSize: 13, color: "#888780", margin: 0 }}>Loading tenant screening settings…</p>
          ) : availablePurposes.length === 0 ? (
            <p style={{ fontSize: 13, color: "#791F1F", margin: 0, padding: "10px 12px", background: "#FCEBEB", borderRadius: 8 }}>
              No scan purposes are enabled for this tenant. Enable at least one under{' '}
              <a href="/compliance-safe/settings/patron-screening" style={{ color: "var(--psg-teal)" }}>Patron Screening Settings</a>.
            </p>
          ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {availablePurposes.map(p => {
              const isSelected = purpose === p.id;
              const relevantCount = catalogSources.filter(s => s.purposes && s.purposes.includes(p.id)).length;
              return (
                <div key={p.id} onClick={() => setPurpose(p.id)}
                  style={{
                    padding: "10px 12px", borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
                    border: isSelected ? `2px solid ${p.color}` : "1px solid #E8E7E2",
                    background: isSelected ? `${p.color}08` : "transparent",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: p.color, fontFamily: "var(--font-mono, monospace)", width: 20, textAlign: "center" }}>{p.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: isSelected ? p.color : "#1f2937" }}>{p.label}</span>
                    </div>
                    <span style={{ fontSize: 11, color: "#888780", background: "#F1EFE8", padding: "1px 6px", borderRadius: 4 }}>{relevantCount}</span>
                  </div>
                  <p style={{ fontSize: 11, color: "#888780", margin: "3px 0 0 28px", lineHeight: 1.3 }}>{p.desc}</p>
                </div>
              );
            })}
          </div>
          )}
          {catalogSettingsLoaded && availablePurposes.length > 0 && purpose !== "full" && (
            <p style={{ fontSize: 12, color: "#888780", margin: "8px 0 0", fontStyle: "italic" }}>
              {filteredSources.length} of {catalogSources.length} enabled sources will be checked — {skippedSources.length} sources not relevant to "{selectedPurpose?.label}" will be skipped
            </p>
          )}
        </div>

        {catalogSettingsLoaded && filteredSources.length === 0 ? (
          <p style={{ fontSize: 13, color: "#791F1F", margin: "0 0 16px", padding: "10px 12px", background: "#FCEBEB", borderRadius: 8 }}>
            No sources are enabled for this purpose. Ask an administrator to update{' '}
            <a href="/compliance-safe/settings/patron-screening" style={{ color: "var(--psg-teal)" }}>Patron Screening Settings</a>.
          </p>
        ) : null}

        {scanning ? (
          <div>
            <div style={{ height: 6, background: "#F1EFE8", borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ width: `${scanProgress}%`, height: "100%", background: "var(--psg-teal)", borderRadius: 3, transition: "width 0.15s" }} />
            </div>
            <p style={{ fontSize: 12, color: "#888780", margin: 0, textAlign: "center" }}>Scanning {filteredSources.length} sources for {selectedPurpose?.label || "full screening"}... {scanProgress}%</p>
          </div>
        ) : (
          <button type="button" onClick={runScan} disabled={!catalogSettingsLoaded || filteredSources.length === 0 || availablePurposes.length === 0} style={{
            width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
            background: (!catalogSettingsLoaded || filteredSources.length === 0 || availablePurposes.length === 0) ? "#D3D1C7" : "var(--psg-teal)", color: "#fff", fontSize: 14, fontWeight: 500,
            cursor: (!catalogSettingsLoaded || filteredSources.length === 0 || availablePurposes.length === 0) ? "not-allowed" : "pointer", transition: "opacity 0.15s"
          }}>
            {scanned ? "Re-run scan" : "Run OSINT scan"}
          </button>
        )}
      </div>

      {/* Results */}
      {scanned && (
        <div>
          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 20 }}>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>Sources checked</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0" }}>{filteredSources.length}</p>
            </div>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>Hits found</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0", color: "#A32D2D" }}>{hits.length}</p>
            </div>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>All clear</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0", color: "#27500A" }}>{clear.length}</p>
            </div>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>Errors</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0", color: "#633806" }}>{errorsByUpstream.length}</p>
            </div>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>Skipped</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0", color: "#888780" }}>{skippedSources.length}</p>
            </div>
            <div style={{ background: "#FAFAF8", borderRadius: 10, padding: 16 }}>
              <p style={{ fontSize: 12, color: "#888780", margin: 0 }}>Risk score</p>
              <p style={{ fontSize: 24, fontWeight: 600, margin: "4px 0 0", color: riskColor }}>{riskScore}</p>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #E8E7E2", marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { key: "hits", label: "Hits requiring action", count: hits.length, bg: "#FCEBEB", color: "#791F1F" },
              { key: "clear", label: "All clear", count: clear.length, bg: "#EAF3DE", color: "#27500A" },
              ...(errorsByUpstream.length > 0 ? [{ key: "errors", label: "Errors / needs key", count: errorsByUpstream.length, bg: "#FAEEDA", color: "#633806" }] : []),
              ...(notIntegrated.length > 0 ? [{ key: "not_integrated", label: "Not integrated", count: notIntegrated.length, bg: "#F1EFE8", color: "#5F5E5A" }] : []),
              ...(skippedSources.length > 0 ? [{ key: "skipped", label: "Skipped (purpose)", count: skippedSources.length, bg: "#F1EFE8", color: "#5F5E5A" }] : []),
            ].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setExpanded(null); }} style={{
                padding: "10px 20px", fontSize: 14, fontWeight: 500, cursor: "pointer",
                border: "none", background: "none",
                color: tab === t.key ? "var(--psg-teal)" : "#888780",
                borderBottom: tab === t.key ? "2px solid var(--psg-teal)" : "2px solid transparent",
                transition: "all 0.15s"
              }}>
                {t.label}
                <span style={{
                  fontSize: 11, padding: "1px 8px", borderRadius: 10, marginLeft: 8, fontWeight: 500,
                  background: t.bg, color: t.color
                }}>{t.count}</span>
              </button>
            ))}
          </div>

          {/* Source list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {tab === "hits" && hits.map((s) => (
              <SourceRow key={s.id} source={s} expanded={expanded === s.id}
                onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                liveStatus={deriveLiveStatus(s, liveReadyMap)} scanned={scanned} />
            ))}
            {tab === "clear" && clear.map((s) => (
              <SourceRow key={s.id} source={s} expanded={false} onToggle={() => {}}
                liveStatus={deriveLiveStatus(s, liveReadyMap)} scanned={scanned} />
            ))}
            {tab === "errors" && errorsByUpstream.map((s) => (
              <SourceRow key={s.id} source={s} expanded={expanded === s.id}
                onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                liveStatus={deriveLiveStatus(s, liveReadyMap)} scanned={scanned} />
            ))}
            {tab === "not_integrated" && notIntegrated.map((s) => {
              const cat = CAT_STYLES[s.cat] || { bg: "#F1EFE8", text: "#444441" };
              return (
                <div key={s.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderRadius: 8, marginBottom: 2,
                  border: "1px solid #E8E7E2", opacity: 0.85,
                }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: "#5F5E5A" }}>{s.name}</span>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: cat.bg, color: cat.text, fontWeight: 500 }}>{s.cat}</span>
                      <LiveStatusBadge status="in_progress" />
                    </div>
                    <p style={{ fontSize: 11, color: "#888780", margin: "4px 0 0", maxWidth: 520 }}>
                      Catalog placeholder only — no live API yet. Disable in Patron Screening Settings if not needed.
                    </p>
                  </div>
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, background: "#F1EFE8", color: "#5F5E5A", fontWeight: 500 }}>Demo</span>
                </div>
              );
            })}
            {tab === "skipped" && skippedSources.map((s) => {
              const cat = CAT_STYLES[s.cat] || { bg: "#F1EFE8", text: "#444441" };
              return (
                <div key={s.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderRadius: 8, marginBottom: 2,
                  border: "1px solid #E8E7E2", opacity: 0.6
                }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: "#888780" }}>{s.name}</span>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: cat.bg, color: cat.text, fontWeight: 500 }}>{s.cat}</span>
                      <LiveStatusBadge status={deriveLiveStatus(s, liveReadyMap)} />
                    </div>
                    <p style={{ fontSize: 11, color: "#B4B2A9", margin: "2px 0 0" }}>Not relevant to "{selectedPurpose?.label}" investigation</p>
                  </div>
                  <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, background: "#F1EFE8", color: "#888780", fontWeight: 500 }}>Skipped</span>
                </div>
              );
            })}
          </div>

          {/* Save to patron record */}
          <div style={{
            marginTop: 20, padding: 14, borderRadius: 10,
            border: "1px solid #E8E7E2", background: "#FAFAF8",
            display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between"
          }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: "#1f2937" }}>
                {saveStatus === "saved" ? "Scan saved to audit log" : "Save scan to audit log"}
              </p>
              <p style={{ fontSize: 11, color: "#888780", margin: "2px 0 0" }}>
                {saveStatus === "saved"
                  ? `Scan ID: ${savedScanUuid || '—'} — available under "Previous scans" here and in the ECDD Search Audit panel.`
                  : patronId
                    ? `Linked to patron ${patronId}. Keeps a permanent record for audit + re-run later.`
                    : "No patron linked — scan will be filed under the subject name only."}
              </p>
              {saveStatus === "error" && saveError && (
                <p style={{ fontSize: 11, color: "#A32D2D", margin: "4px 0 0" }}>Error: {saveError}</p>
              )}
            </div>
            <button type="button" onClick={handleSaveScan}
              disabled={saveStatus === "saving" || saveStatus === "saved"}
              style={{
                padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500,
                border: "1px solid " + (saveStatus === "saved" ? "#27500A" : "var(--psg-teal)"),
                background: saveStatus === "saved" ? "#EAF3DE" : "var(--psg-teal)",
                color: saveStatus === "saved" ? "#27500A" : "#fff",
                cursor: (saveStatus === "saving" || saveStatus === "saved") ? "default" : "pointer",
                opacity: saveStatus === "saving" ? 0.7 : 1, whiteSpace: "nowrap", flexShrink: 0,
              }}>
              {saveStatus === "saving" ? "Saving..."
                : saveStatus === "saved" ? "✓ Saved"
                : saveStatus === "error" ? "Retry save"
                : "Save scan"}
            </button>
          </div>

          {/* Audit trail */}
          <AuditTrail entries={auditEntries} />
        </div>
      )}
    </div>
  );
}
